import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type RequestState = { pending: number; failed: boolean };
type ReportLoadingValue = {
  loading: boolean;
  error: boolean;
  revision: number;
  retry: () => void;
  update: (id: symbol, state?: RequestState) => void;
};
const noop = () => { };
const ReportLoadingContext = createContext<ReportLoadingValue>({ loading: false, error: false, revision: 0, retry: noop, update: noop });

export function ReportLoadingProvider({ children }: { children: ReactNode }) {
  const requests = useRef(new Map<symbol, RequestState>());
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const [state, setState] = useState({ loading: false, error: false });
  const [revision, setRevision] = useState(0);
  const update = useCallback((id: symbol, value?: RequestState) => {
    if (value) requests.current.set(id, value);
    else requests.current.delete(id);
    if (timer.current) clearTimeout(timer.current);
    const publish = () => {
      const values = Array.from(requests.current.values());
      const next = { loading: values.some(v => v.pending > 0), error: values.some(v => v.failed) };
      setState(prev => prev.loading === next.loading && prev.error === next.error ? prev : next);
    };
    // Let dependent effects start before publishing an idle state.
    if (Array.from(requests.current.values()).some(v => v.pending > 0)) publish();
    else timer.current = setTimeout(publish, 0);
  }, []);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const retry = useCallback(() => setRevision(v => v + 1), []);
  const value = useMemo(() => ({ ...state, revision, retry, update }), [state, revision, retry, update]);
  return <ReportLoadingContext.Provider value={value}>{children}</ReportLoadingContext.Provider>;
}
export const useReportLoading = () => useContext(ReportLoadingContext);

export const ReportExportContext = createContext(false);
export const useReportExportDisabled = () => useContext(ReportExportContext);
